import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { TicketsAPI } from "../../../api/tickets";
import {
  DepartmentsAPI,
  CategoriesAPI,
} from "../../../api/lookups";
import { StatusAPI } from "../../../api/statuses";

const DEFAULT_SORT = "updated_at";
const DEFAULT_DIRECTION = "desc";

const GROUP_OPTIONS = [
  { value: "", label: "No grouping" },
  { value: "requester_name", label: "Raised by" },
  { value: "assignee_names", label: "Assigned to" },
  { value: "school_name", label: "School" },
  { value: "department_name", label: "Department" },
  { value: "category_name", label: "Category" },
  { value: "status_name", label: "Status" },
];

function filtersFromSearchParams(searchParams) {
  return {
    search: searchParams.get("search") || "",
    school_id: searchParams.get("school_id") || "",
    status_id: searchParams.get("status_id") || "",
    department_id: searchParams.get("department_id") || "",
    category_id: searchParams.get("category_id") || "",
    assigned_to: searchParams.get("assigned_to") || "",
    raised_by: searchParams.get("raised_by") || "",
    open_only: searchParams.has("open_only")
      ? searchParams.get("open_only") || ""
      : "1",
    stale_days: searchParams.get("stale_days") || "",
  };
}

function sortFromSearchParams(searchParams) {
  return {
    sort: searchParams.get("sort") || DEFAULT_SORT,
    direction:
      searchParams.get("direction") === "asc"
        ? "asc"
        : DEFAULT_DIRECTION,
  };
}

function getSortValue(ticket, field) {
  switch (field) {
    case "id":
      return Number(ticket.id) || 0;

    case "title":
      return ticket.title || "";

    case "requester_name":
      return ticket.requester_name || "";

    case "assignee_names":
      return ticket.assignee_names || "Unassigned";

    case "school_name":
      return ticket.school_name || "";

    case "department_name":
      return ticket.department_name || "";

    case "category_name":
      return ticket.category_name || "";

    case "status_name":
      return ticket.status_name || "";

    case "updated_at":
      return ticket.updated_at
        ? new Date(ticket.updated_at).getTime()
        : 0;

    default:
      return "";
  }
}

function getGroupLabel(ticket, groupBy) {
  switch (groupBy) {
    case "requester_name":
      return ticket.requester_name || "No requester";

    case "assignee_names":
      return ticket.assignee_names || "Unassigned";

    case "school_name":
      return ticket.school_name || "No school";

    case "department_name":
      return ticket.department_name || "No department";

    case "category_name":
      return ticket.category_name || "No category";

    case "status_name":
      return ticket.status_name || "No status";

    default:
      return "Tickets";
  }
}

export default function TicketList() {
  const {state:routeState}=useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tickets, setTickets] = useState([]);
  const [schools, setSchools] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [requesters, setRequesters] = useState([]);

  const [searchText, setSearchText] = useState(
    searchParams.get("search") || ""
  );

  const [collapsedGroups, setCollapsedGroups] = useState(
    () => new Set()
  );

  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const filters = useMemo(
    () => filtersFromSearchParams(searchParams),
    [searchParams]
  );

  const sortSettings = useMemo(
    () => sortFromSearchParams(searchParams),
    [searchParams]
  );

  const groupBy = searchParams.get("group_by") || "";

  const updateFilters = (changes) => {
    const params = new URLSearchParams(searchParams);

    Object.entries(changes).forEach(([key, value]) => {
      if (key === "open_only") {
        if (value === "1") {
          params.delete("open_only");
        } else {
          params.set("open_only", "");
        }

        return;
      }

      if (
        value === undefined ||
        value === null ||
        value === ""
      ) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });

    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchText("");

    const params = new URLSearchParams();

    if (
      sortSettings.sort !== DEFAULT_SORT ||
      sortSettings.direction !== DEFAULT_DIRECTION
    ) {
      params.set("sort", sortSettings.sort);
      params.set(
        "direction",
        sortSettings.direction
      );
    }

    if (groupBy) {
      params.set("group_by", groupBy);
    }

    setSearchParams(params);
  };

  const handleSort = (field) => {
    const params = new URLSearchParams(searchParams);

    let nextDirection = "asc";

    if (sortSettings.sort === field) {
      nextDirection =
        sortSettings.direction === "asc"
          ? "desc"
          : "asc";
    } else if (
      field === "updated_at" ||
      field === "id"
    ) {
      nextDirection = "desc";
    }

    if (
      field === DEFAULT_SORT &&
      nextDirection === DEFAULT_DIRECTION
    ) {
      params.delete("sort");
      params.delete("direction");
    } else {
      params.set("sort", field);
      params.set("direction", nextDirection);
    }

    setSearchParams(params);
  };

  const handleGroupChange = (value) => {
    const params = new URLSearchParams(searchParams);

    if (value) {
      params.set("group_by", value);
    } else {
      params.delete("group_by");
    }

    setCollapsedGroups(new Set());
    setSearchParams(params);
  };

  const toggleGroup = (groupName) => {
    setCollapsedGroups((current) => {
      const next = new Set(current);

      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }

      return next;
    });
  };

  const renderSortHeading = (
    label,
    field,
    className = ""
  ) => {
    const active = sortSettings.sort === field;

    return (
      <th
        className={`px-3 py-2 text-left ${className}`}
      >
        <button
          type="button"
          onClick={() => handleSort(field)}
          className="inline-flex items-center gap-1 font-semibold hover:text-blue-600"
          title={`Sort by ${label}`}
        >
          <span>{label}</span>

          {active && (
            <span
              className="text-xs text-blue-600"
              aria-hidden="true"
            >
              {sortSettings.direction === "asc"
                ? "↑"
                : "↓"}
            </span>
          )}
        </button>
      </th>
    );
  };

  // ------------------------------------------------------------
  // Load stable filter options once
  // ------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    Promise.all([
      TicketsAPI.allowedSchools("tickets.view"),
      StatusAPI.list(),
      DepartmentsAPI.list(),
      CategoriesAPI.list(),
      TicketsAPI.filterOptions(),
    ])
      .then(
        ([
          schoolRows,
          statusRows,
          departmentRows,
          categoryRows,
          filterOptions,
        ]) => {
          if (cancelled) return;

          setSchools(schoolRows || []);
          setStatuses(statusRows || []);
          setDepartments(departmentRows || []);
          setCategories(categoryRows || []);
          setAssignees(filterOptions?.assignees || []);
          setRequesters(filterOptions?.requesters || []);
        }
      )
      .catch((err) => {
        if (cancelled) return;

        setError(
          err.message ||
            "Failed to load ticket filter options"
        );
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // ------------------------------------------------------------
  // Keep search input synced with URL
  // ------------------------------------------------------------
  useEffect(() => {
    setSearchText(filters.search);
  }, [filters.search]);

  // ------------------------------------------------------------
  // Load tickets when applied filters change
  // ------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    setError(null);

    if (!initialLoading) {
      setRefreshing(true);
    }

    TicketsAPI.list(filters)
      .then((rows) => {
        if (cancelled) return;
        setTickets(rows || []);
      })
      .catch((err) => {
        if (cancelled) return;

        setError(
          err.message || "Failed to load tickets"
        );
      })
      .finally(() => {
        if (cancelled) return;

        setInitialLoading(false);
        setRefreshing(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    filters.search,
    filters.school_id,
    filters.status_id,
    filters.department_id,
    filters.category_id,
    filters.assigned_to,
    filters.raised_by,
    filters.open_only,
    filters.stale_days,
  ]);

  // ------------------------------------------------------------
  // Debounced search
  // ------------------------------------------------------------
  useEffect(() => {
    if (searchText === filters.search) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      updateFilters({
        search: searchText,
      });
    }, 350);

    return () => clearTimeout(timeout);
  }, [searchText, filters.search]);

  // ------------------------------------------------------------
  // Auto-refresh every 30 seconds
  // ------------------------------------------------------------
  useEffect(() => {
    const interval = setInterval(() => {
      const currentFilters =
        filtersFromSearchParams(searchParams);

      TicketsAPI.list(currentFilters)
        .then((rows) => {
          setTickets(rows || []);
        })
        .catch((err) => {
          console.error(
            "Auto-refresh failed",
            err
          );
        });
    }, 30000);

    return () => clearInterval(interval);
  }, [searchParams]);

  // ------------------------------------------------------------
  // Department -> Category
  // ------------------------------------------------------------
  const categoryOptions = filters.department_id
    ? categories.filter(
        (category) =>
          String(category.department_id || "") ===
          String(filters.department_id)
      )
    : categories;

  // ------------------------------------------------------------
  // Department column visibility
  // ------------------------------------------------------------
  const departmentNames = new Set(
    tickets
      .map((ticket) =>
        String(ticket.department_name || "")
      )
      .filter(Boolean)
  );

  const showDepartmentColumn =
    departmentNames.size > 1 ||
    Boolean(filters.department_id) ||
    groupBy === "department_name";

  // ------------------------------------------------------------
  // Sort tickets
  // ------------------------------------------------------------
  const sortedTickets = useMemo(() => {
    const rows = [...tickets];

    rows.sort((a, b) => {
      const aValue = getSortValue(
        a,
        sortSettings.sort
      );

      const bValue = getSortValue(
        b,
        sortSettings.sort
      );

      let comparison = 0;

      if (
        typeof aValue === "number" &&
        typeof bValue === "number"
      ) {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(
          String(bValue),
          undefined,
          {
            numeric: true,
            sensitivity: "base",
          }
        );
      }

      if (comparison === 0) {
        comparison =
          (Number(b.id) || 0) -
          (Number(a.id) || 0);
      }

      return sortSettings.direction === "asc"
        ? comparison
        : -comparison;
    });

    return rows;
  }, [
    tickets,
    sortSettings.sort,
    sortSettings.direction,
  ]);

  // ------------------------------------------------------------
  // Group tickets
  // ------------------------------------------------------------
  const groupedTickets = useMemo(() => {
    if (!groupBy) {
      return [];
    }

    const groups = new Map();

    sortedTickets.forEach((ticket) => {
      const label = getGroupLabel(
        ticket,
        groupBy
      );

      if (!groups.has(label)) {
        groups.set(label, []);
      }

      groups.get(label).push(ticket);
    });

    return Array.from(groups.entries()).sort(
      ([a], [b]) =>
        a.localeCompare(b, undefined, {
          numeric: true,
          sensitivity: "base",
        })
    );
  }, [sortedTickets, groupBy]);

  // ------------------------------------------------------------
  // Active filter count
  // ------------------------------------------------------------
  const activeFilterCount = [
    filters.search,
    filters.school_id,
    filters.status_id,
    filters.department_id,
    filters.category_id,
    filters.assigned_to,
    filters.raised_by,
    filters.stale_days,
    filters.open_only !== "1" ? "all" : "",
  ].filter(Boolean).length;

  const renderTableHead = () => (
    <thead className="bg-slate-100">
      <tr>
        {renderSortHeading("#", "id", "w-16")}

        {renderSortHeading(
          "Title",
          "title"
        )}

        {renderSortHeading(
          "Raised by",
          "requester_name"
        )}

        {renderSortHeading(
          "Assigned to",
          "assignee_names"
        )}

        {renderSortHeading(
          "School",
          "school_name"
        )}

        {showDepartmentColumn &&
          renderSortHeading(
            "Department",
            "department_name"
          )}

        {renderSortHeading(
          "Category",
          "category_name"
        )}

        {renderSortHeading(
          "Status",
          "status_name"
        )}

        {renderSortHeading(
          "Last updated",
          "updated_at"
        )}

        <th className="w-10 px-3 py-2 text-right" />
      </tr>
    </thead>
  );

  const renderTicketRow = (ticket) => (
    <tr
      key={ticket.id}
      className="border-t hover:bg-slate-50"
    >
      <td className="px-3 py-2 align-top text-slate-500">
        {ticket.id}
      </td>

      <td className="px-3 py-2 align-top">
        <Link
          to={`/helpdesk/tickets/${ticket.id}`}
          className="font-medium text-blue-600 hover:underline"
        >
          {ticket.title}
        </Link>

        <div className="mt-0.5 text-xs text-slate-500">
          {ticket.created_at
            ? new Date(
                ticket.created_at
              ).toLocaleString()
            : ""}
        </div>
      </td>

      <td className="px-3 py-2 align-top">
        {ticket.requester_name || "-"}
      </td>

      <td className="px-3 py-2 align-top">
        {ticket.assignee_names || (
          <span className="text-slate-500">
            Unassigned
          </span>
        )}
      </td>

      <td className="px-3 py-2 align-top">
        {ticket.school_name || "-"}
      </td>

      {showDepartmentColumn && (
        <td className="px-3 py-2 align-top">
          {ticket.department_name || "-"}
        </td>
      )}

      <td className="px-3 py-2 align-top">
        {ticket.category_name || "-"}
      </td>

      <td className="px-3 py-2 align-top">
        {ticket.status_name || "-"}
      </td>

      <td className="whitespace-nowrap px-3 py-2 align-top">
        {ticket.updated_at
          ? new Date(
              ticket.updated_at
            ).toLocaleString()
          : "-"}
      </td>

      <td className="px-3 py-2 text-right align-top">
        {ticket.has_unread_mentions && (
          <span
            className="inline-block h-2 w-2 rounded-full bg-red-500"
            title="Unread mention"
          />
        )}
      </td>
    </tr>
  );

  if (initialLoading) {
    return (
      <div className="p-4">
        <p className="text-sm text-slate-600">
          Loading tickets...
        </p>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}

      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          {routeState?.ticketTransferred && <p role="status" className="mb-3">Ticket transferred successfully. It will only remain visible if you have access to its destination.</p>}
      <h1 className="text-xl font-bold">
            Tickets
          </h1>

          <div className="mt-1 text-sm text-slate-500">
            {tickets.length} ticket
            {tickets.length === 1 ? "" : "s"}

            {refreshing && (
              <span className="ml-2 text-slate-400">
                Refreshing...
              </span>
            )}
          </div>
        </div>

        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={clearFilters}
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Clear filters
          </button>
        )}
      </div>

            {/* View controls */}

          {tickets.length > 0 && (
            <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
              <label
                htmlFor="ticket-group-by"
                className="text-sm font-medium text-slate-600"
              >
                Group by
              </label>

              <select
                id="ticket-group-by"
                value={groupBy}
                onChange={(e) =>
                  handleGroupChange(
                    e.target.value
                  )
                }
                className="rounded border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                {GROUP_OPTIONS.map(
                  (option) => (
                    <option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  )
                )}
              </select>
            </div>
          )}

      {/* Filters */}

      <div className="mb-4 rounded-lg border bg-slate-50 p-3">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
          <input
            type="text"
            placeholder="Search tickets..."
            value={searchText}
            onChange={(e) =>
              setSearchText(e.target.value)
            }
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm"
          />

          <select
            value={filters.school_id}
            onChange={(e) =>
              updateFilters({
                school_id: e.target.value,
              })
            }
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">
              All schools
            </option>

            {schools.map((school) => (
              <option
                key={school.id}
                value={school.id}
              >
                {school.name}
              </option>
            ))}
          </select>

          <select
            value={filters.status_id}
            onChange={(e) =>
              updateFilters({
                status_id: e.target.value,
              })
            }
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">
              All statuses
            </option>

            {statuses.map((status) => (
              <option
                key={status.id}
                value={status.id}
              >
                {status.name}
              </option>
            ))}
          </select>

          <select
            value={filters.department_id}
            onChange={(e) =>
              updateFilters({
                department_id:
                  e.target.value,
                category_id: "",
              })
            }
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">
              All departments
            </option>

            {departments.map(
              (department) => (
                <option
                  key={department.id}
                  value={department.id}
                >
                  {department.name}
                </option>
              )
            )}
          </select>

          <select
            value={filters.category_id}
            onChange={(e) =>
              updateFilters({
                category_id:
                  e.target.value,
              })
            }
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">
              All categories
            </option>

            {categoryOptions.map(
              (category) => (
                <option
                  key={category.id}
                  value={category.id}
                >
                  {category.name}
                </option>
              )
            )}
          </select>

          <select
            value={filters.assigned_to}
            onChange={(e) =>
              updateFilters({
                assigned_to:
                  e.target.value,
              })
            }
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">
              All assignees
            </option>

            <option value="__unassigned__">
              Unassigned
            </option>

            {assignees.map((user) => (
              <option
                key={user.id}
                value={user.id}
              >
                {user.name}
              </option>
            ))}
          </select>

          <select
            value={filters.raised_by}
            onChange={(e) =>
              updateFilters({
                raised_by:
                  e.target.value,
              })
            }
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">
              All requesters
            </option>

            {requesters.map((user) => (
              <option
                key={user.id}
                value={user.id}
              >
                {user.name}
              </option>
            ))}
          </select>

          <select
            value={filters.open_only}
            onChange={(e) =>
              updateFilters({
                open_only:
                  e.target.value,
              })
            }
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm"
          >
            <option value="1">
              Open tickets only
            </option>

            <option value="">
              All tickets
            </option>
          </select>
        </div>
      </div>

      {/* Stale filter */}

      {filters.stale_days && (
        <div className="mb-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() =>
              updateFilters({
                stale_days: "",
              })
            }
            className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-200"
          >
            Not updated in{" "}
            {filters.stale_days}+ days
            <span aria-hidden="true">
              ×
            </span>
          </button>
        </div>
      )}

      {/* Error */}

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Empty state */}

      {!tickets.length ? (
        <div className="rounded-lg border bg-white px-4 py-8 text-center">
          <p className="text-sm font-medium text-slate-700">
            No tickets found
          </p>

          <p className="mt-1 text-sm text-slate-500">
            Try changing or clearing your
            filters.
          </p>
        </div>
      ) : !groupBy ? (
        /* Normal table */

        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="min-w-full text-sm">
            {renderTableHead()}

            <tbody>
              {sortedTickets.map(
                renderTicketRow
              )}
            </tbody>
          </table>
        </div>
      ) : (
        /* Grouped tables */

        <div className="space-y-3">
          {groupedTickets.map(
            ([groupName, groupTickets]) => {
              const collapsed =
                collapsedGroups.has(
                  groupName
                );

              return (
                <div
                  key={groupName}
                  className="overflow-hidden rounded-lg border bg-white"
                >
                  <button
                    type="button"
                    onClick={() =>
                      toggleGroup(groupName)
                    }
                    className="flex w-full items-center justify-between gap-4 bg-slate-100 px-4 py-3 text-left hover:bg-slate-200"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="w-4 text-xs text-slate-500"
                        aria-hidden="true"
                      >
                        {collapsed
                          ? "▶"
                          : "▼"}
                      </span>

                      <span className="font-semibold text-slate-800">
                        {groupName}
                      </span>
                    </div>

                    <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-600">
                      {groupTickets.length}{" "}
                      ticket
                      {groupTickets.length ===
                      1
                        ? ""
                        : "s"}
                    </span>
                  </button>

                  {!collapsed && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        {renderTableHead()}

                        <tbody>
                          {groupTickets.map(
                            renderTicketRow
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            }
          )}
        </div>
      )}
    </div>
  );
}